import type { BotSchedule, BotScopeSchedule, OperatorAccount, Project, Site } from './types.js';

const workspace = document.getElementById('bot-schedule-workspace') as HTMLElement;
let selectedBotId = '';

export function renderBotScheduleAdmin(
  operators: OperatorAccount[],
  projects: Project[],
  sites: Site[],
  save: (botId: string, input: { defaultSchedule: BotSchedule; scopeSchedules: BotScopeSchedule[] }) => Promise<void>
): void {
  const bots = operators.filter((operator) => operator.role === 'bot' && operator.suvvyIntegration);
  workspace.replaceChildren();
  if (bots.length === 0) {
    workspace.append(emptyState('Сначала создайте и назначьте ИИ-бота в разделе «Операторы и боты».'));
    return;
  }
  if (!bots.some((bot) => bot.id === selectedBotId)) selectedBotId = bots[0]!.id;
  const bot = bots.find((item) => item.id === selectedBotId)!;
  const integration = bot.suvvyIntegration!;

  const toolbar = document.createElement('div');
  toolbar.className = 'schedule-toolbar';
  const botLabel = document.createElement('label');
  botLabel.textContent = 'ИИ-бот';
  const botSelect = document.createElement('select');
  for (const item of bots) {
    const option = document.createElement('option');
    option.value = item.id;
    option.textContent = item.displayName;
    botSelect.append(option);
  }
  botSelect.value = bot.id;
  botSelect.addEventListener('change', () => {
    selectedBotId = botSelect.value;
    renderBotScheduleAdmin(operators, projects, sites, save);
  });
  botLabel.append(botSelect);
  const summary = document.createElement('div');
  summary.innerHTML = `<span>BOT / SCHEDULE</span><strong>${integration.scopeSchedules.length} правил переопределения</strong>`;
  toolbar.append(botLabel, summary);

  const form = document.createElement('form');
  form.className = 'schedule-form';
  const baseCard = document.createElement('section');
  baseCard.className = 'schedule-base-card';
  const baseHeading = document.createElement('div');
  baseHeading.className = 'schedule-section-heading';
  const baseCopy = document.createElement('div');
  const baseEyebrow = document.createElement('p');
  baseEyebrow.textContent = 'DEFAULT / ALL ASSIGNMENTS';
  const baseTitle = document.createElement('h2');
  baseTitle.textContent = 'Базовое расписание';
  const baseDescription = document.createElement('span');
  baseDescription.textContent = 'Применяется ко всем назначениям без отдельного правила.';
  baseCopy.append(baseEyebrow, baseTitle, baseDescription);
  baseHeading.append(baseCopy);
  const baseEditor = createScheduleEditor(integration.schedule, false);
  baseCard.append(baseHeading, baseEditor);

  const overrides = document.createElement('section');
  overrides.className = 'schedule-overrides';
  const overrideHeading = document.createElement('div');
  overrideHeading.className = 'schedule-section-heading';
  const overrideCopy = document.createElement('div');
  const overrideEyebrow = document.createElement('p');
  overrideEyebrow.textContent = 'OVERRIDES / PRIORITY';
  const overrideTitle = document.createElement('h2');
  overrideTitle.textContent = 'Проекты и сайты';
  const overrideDescription = document.createElement('span');
  overrideDescription.textContent = 'Сайт переопределяет проект. «Наследовать» использует ближайшее правило выше.';
  overrideCopy.append(overrideEyebrow, overrideTitle, overrideDescription);
  overrideHeading.append(overrideCopy);
  overrides.append(overrideHeading);

  const projectScopes = projects.filter((project) => bot.projectIds.includes(project.id));
  const siteScopes = sites.filter((site) => bot.siteIds.includes(site.id)
    || Boolean(site.projectId && bot.projectIds.includes(site.projectId)));
  const cards = document.createElement('div');
  cards.className = 'schedule-scope-list';
  for (const project of projectScopes) {
    cards.append(scopeCard('project', project.id, project.name, 'Проект · все его сайты', integration.scopeSchedules));
  }
  for (const site of siteScopes) {
    cards.append(scopeCard('site', site.id, site.name, `Сайт · ${site.projectName ?? site.domain ?? 'без проекта'}`, integration.scopeSchedules));
  }
  if (!cards.children.length) cards.append(emptyState('У бота пока нет назначенных проектов или сайтов.'));
  overrides.append(cards);

  const actions = document.createElement('div');
  actions.className = 'schedule-actions';
  const submit = document.createElement('button');
  submit.type = 'submit';
  submit.textContent = 'Сохранить расписания';
  const status = document.createElement('p');
  status.className = 'admin-form__status';
  status.setAttribute('role', 'status');
  actions.append(submit, status);
  form.append(baseCard, overrides, actions);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      submit.disabled = true;
      status.textContent = 'Сохраняем правила…';
      try {
        const scopeSchedules = Array.from(form.querySelectorAll<HTMLElement>('[data-schedule-scope]'))
          .flatMap((card): BotScopeSchedule[] => {
            const editor = card.querySelector<HTMLElement>('[data-schedule-editor]')!;
            if ((editor.querySelector('select') as HTMLSelectElement).value === 'inherit') return [];
            return [{
              scopeType: card.dataset.scopeType as BotScopeSchedule['scopeType'],
              scopeId: card.dataset.scopeId!,
              schedule: readSchedule(editor)
            }];
          });
        await save(bot.id, { defaultSchedule: readSchedule(baseEditor), scopeSchedules });
        status.textContent = 'Расписания сохранены';
      } catch (error) {
        status.textContent = error instanceof Error ? error.message : 'Не удалось сохранить расписания';
      } finally {
        submit.disabled = false;
      }
    })();
  });
  workspace.append(toolbar, form);
}

function scopeCard(
  scopeType: BotScopeSchedule['scopeType'],
  scopeId: string,
  titleText: string,
  noteText: string,
  schedules: BotScopeSchedule[]
): HTMLElement {
  const card = document.createElement('article');
  card.className = `schedule-scope-card schedule-scope-card--${scopeType}`;
  card.dataset.scheduleScope = '';
  card.dataset.scopeType = scopeType;
  card.dataset.scopeId = scopeId;
  const heading = document.createElement('div');
  heading.className = 'schedule-scope-card__heading';
  const marker = document.createElement('span');
  marker.textContent = scopeType === 'project' ? 'P' : 'S';
  const copy = document.createElement('div');
  const title = document.createElement('h3');
  title.textContent = titleText;
  const note = document.createElement('p');
  note.textContent = noteText;
  copy.append(title, note);
  heading.append(marker, copy);
  const existing = schedules.find((item) => item.scopeType === scopeType && item.scopeId === scopeId);
  card.append(heading, createScheduleEditor(existing?.schedule ?? defaultSchedule(), true, existing ? existing.schedule.mode : 'inherit'));
  return card;
}

function createScheduleEditor(
  schedule: BotSchedule,
  allowInherit: boolean,
  initialMode: BotSchedule['mode'] | 'inherit' = schedule.mode
): HTMLElement {
  const root = document.createElement('div');
  root.className = 'schedule-editor';
  root.dataset.scheduleEditor = '';
  const modeLabel = document.createElement('label');
  modeLabel.textContent = 'Режим';
  const mode = document.createElement('select');
  if (allowInherit) mode.append(option('inherit', 'Наследовать'));
  mode.append(option('always', 'Всегда отвечает'), option('custom', 'По расписанию'));
  mode.value = initialMode;
  modeLabel.append(mode);

  const custom = document.createElement('div');
  custom.className = 'schedule-editor__custom';
  const timezone = document.createElement('label');
  timezone.className = 'schedule-timezone-field';
  timezone.textContent = 'Часовой пояс';
  const timezoneInput = document.createElement('input');
  timezoneInput.className = 'schedule-timezone';
  timezoneInput.value = schedule.timezone;
  timezoneInput.maxLength = 100;
  timezoneInput.required = true;
  timezoneInput.setAttribute('list', 'bot-timezones');
  timezone.append(timezoneInput);
  custom.append(timezone, dayEditor('Будни', 'Пн–Пт', schedule.weekdays), dayEditor('Выходные', 'Сб–Вс', schedule.weekends));
  const note = document.createElement('small');
  note.className = 'bot-schedule__note';
  note.textContent = 'Если время «С» позже времени «До», бот работает через полночь. Одинаковое время — круглосуточно.';
  custom.append(note);
  mode.addEventListener('change', () => syncEditor(root));
  root.append(modeLabel, custom);
  syncEditor(root);
  return root;
}

function dayEditor(title: string, note: string, value: BotSchedule['weekdays']): HTMLElement {
  const day = document.createElement('div');
  day.className = 'schedule-day';
  const toggle = document.createElement('label');
  toggle.className = 'schedule-day__toggle';
  const enabled = document.createElement('input');
  enabled.type = 'checkbox';
  enabled.checked = value.enabled;
  enabled.className = 'schedule-day-enabled';
  const copy = document.createElement('span');
  const strong = document.createElement('strong');
  strong.textContent = title;
  const small = document.createElement('small');
  small.textContent = note;
  copy.append(strong, small);
  toggle.append(enabled, copy);
  day.append(toggle, timeLabel('С', value.start, 'schedule-day-start'), timeLabel('До', value.end, 'schedule-day-end'));
  return day;
}

function timeLabel(text: string, value: string, className: string): HTMLLabelElement {
  const label = document.createElement('label');
  label.textContent = text;
  const input = document.createElement('input');
  input.type = 'text';
  input.inputMode = 'numeric';
  input.value = value;
  input.placeholder = 'ЧЧ:ММ';
  input.pattern = '(?:[01][0-9]|2[0-3]):[0-5][0-9]';
  input.maxLength = 5;
  input.required = true;
  input.className = className;
  input.title = 'Время в 24-часовом формате, например 09:00';
  bindTime24Input(input);
  label.append(input);
  return label;
}

function readSchedule(root: HTMLElement): BotSchedule {
  const days = root.querySelectorAll<HTMLElement>('.schedule-day');
  const readDay = (day: HTMLElement) => ({
    enabled: day.querySelector<HTMLInputElement>('.schedule-day-enabled')!.checked,
    start: day.querySelector<HTMLInputElement>('.schedule-day-start')!.value,
    end: day.querySelector<HTMLInputElement>('.schedule-day-end')!.value
  });
  return {
    mode: (root.querySelector('select') as HTMLSelectElement).value === 'custom' ? 'custom' : 'always',
    timezone: root.querySelector<HTMLInputElement>('.schedule-timezone')!.value.trim(),
    weekdays: readDay(days[0]!),
    weekends: readDay(days[1]!)
  };
}

function syncEditor(root: HTMLElement): void {
  const custom = root.querySelector<HTMLElement>('.schedule-editor__custom')!;
  const enabled = (root.querySelector('select') as HTMLSelectElement).value === 'custom';
  custom.classList.toggle('is-hidden', !enabled);
  custom.querySelectorAll<HTMLInputElement>('input').forEach((input) => { input.disabled = !enabled; });
}

function bindTime24Input(input: HTMLInputElement): void {
  input.addEventListener('input', () => {
    input.value = input.value.replace(/[^\d:]/g, '').slice(0, 5);
    const digits = input.value.replace(/\D/g, '');
    if (!input.value.includes(':') && digits.length === 4) input.value = `${digits.slice(0, 2)}:${digits.slice(2)}`;
    validateTime24(input);
  });
  input.addEventListener('blur', () => {
    const digits = input.value.replace(/\D/g, '');
    if (digits.length === 3) input.value = `0${digits[0]}:${digits.slice(1)}`;
    else if (digits.length === 4) input.value = `${digits.slice(0, 2)}:${digits.slice(2)}`;
    validateTime24(input);
  });
}

function validateTime24(input: HTMLInputElement): void {
  input.setCustomValidity(/^([01]\d|2[0-3]):[0-5]\d$/.test(input.value)
    ? '' : 'Введите время в 24-часовом формате ЧЧ:ММ');
}

function option(value: string, label: string): HTMLOptionElement {
  const node = document.createElement('option');
  node.value = value;
  node.textContent = label;
  return node;
}

function defaultSchedule(): BotSchedule {
  return {
    mode: 'always', timezone: 'Asia/Yekaterinburg',
    weekdays: { enabled: true, start: '09:00', end: '18:00' },
    weekends: { enabled: true, start: '10:00', end: '16:00' }
  };
}

function emptyState(text: string): HTMLElement {
  const node = document.createElement('p');
  node.className = 'admin-empty';
  node.textContent = text;
  return node;
}
